import { AlertCircle, Info, TriangleAlert } from 'lucide-react';
import { createElement, forwardRef } from 'react';
import { cn } from '../../lib/cn';
import type { DataStateProps, DataStateScale, DataStateTone } from './data-state.types';

const toneIconClasses: Record<'neutral' | 'info' | 'warn' | 'danger', string> = {
  neutral: 'bg-[var(--a-surface-2)] text-[var(--a-text-soft)]',
  info: 'bg-[var(--a-info-soft)] text-[var(--a-info)]',
  warn: 'bg-[var(--a-warning-soft)] text-[var(--a-warning)]',
  danger: 'bg-[var(--a-danger-soft)] text-[var(--a-danger)]',
};

const scaleClasses: Record<DataStateScale, string> = {
  inline: 'flex items-start gap-3 rounded-[10px] border px-3 py-2.5 text-left',
  compact:
    'flex min-h-[3rem] flex-col items-center justify-center rounded-lg border border-dashed px-4 py-3 text-center',
  panel: 'flex flex-col items-center rounded-[14px] border px-6 py-9 text-center',
  page: 'flex min-h-[min(64vh,32rem)] flex-col items-center justify-center rounded-[14px] border px-6 py-10 text-center sm:px-8',
};

const toneIcons: Record<'neutral' | 'info' | 'warn' | 'danger', typeof Info> = {
  neutral: Info,
  info: Info,
  warn: TriangleAlert,
  danger: AlertCircle,
};

function normalizeTone(tone: DataStateTone): 'neutral' | 'info' | 'warn' | 'danger' {
  return tone === 'warning' ? 'warn' : tone;
}

function DefaultIcon({ tone }: { tone: 'neutral' | 'info' | 'warn' | 'danger' }) {
  const Icon = toneIcons[tone];
  return <Icon className="h-[18px] w-[18px]" aria-hidden="true" />;
}

export const DataState = forwardRef<HTMLDivElement, DataStateProps>(
  (
    {
      className,
      title,
      description,
      tone = 'neutral',
      scale = 'panel',
      icon,
      detail,
      detailLabel,
      primaryAction,
      secondaryAction,
      action,
      footer,
      live = 'off',
      headingLevel = scale === 'page' ? 1 : scale === 'compact' ? 3 : 2,
      role,
      ...props
    },
    ref,
  ) => {
    const normalizedTone = normalizeTone(tone);
    const resolvedRole =
      role ?? (live === 'assertive' ? 'alert' : live === 'polite' ? 'status' : undefined);
    const resolvedPrimaryAction = primaryAction ?? action;
    const Heading = `h${headingLevel}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
    const resolvedIcon =
      icon ??
      (scale === 'inline' || scale === 'compact' ? null : <DefaultIcon tone={normalizedTone} />);

    return (
      <div
        ref={ref}
        data-component="data-state"
        data-scale={scale}
        data-tone={normalizedTone}
        role={resolvedRole}
        aria-live={live === 'off' ? undefined : live}
        className={cn(
          'border-[var(--a-line)] bg-[var(--a-surface)] [font-family:var(--a-body)]',
          scaleClasses[scale],
          className,
        )}
        {...props}
      >
        {resolvedIcon ? (
          <div
            aria-hidden="true"
            className={cn(
              'grid h-10 w-10 shrink-0 place-items-center rounded-[10px]',
              toneIconClasses[normalizedTone],
            )}
          >
            {resolvedIcon}
          </div>
        ) : null}

        <div className={cn('min-w-0', scale !== 'inline' && 'flex w-full flex-col items-center')}>
          {createElement(
            Heading,
            {
              className:
                scale === 'compact'
                  ? '[font-family:var(--a-body)] text-[12.5px] font-normal tracking-normal text-[var(--a-text-faint)]'
                  : cn(
                      '[font-family:var(--a-display)] font-semibold tracking-[-0.01em] text-[var(--a-text)]',
                      scale === 'inline' ? 'text-[13px]' : scale === 'page' ? 'text-xl' : 'text-lg',
                    ),
            },
            title,
          )}
          {description ? (
            <p
              className={cn(
                'mt-1.5 max-w-xl text-[13px] leading-6 text-[var(--a-text-soft)]',
                scale === 'inline' && 'leading-5',
                scale === 'compact' && 'mt-1 text-[12px] leading-5 text-[var(--a-text-faint)]',
              )}
            >
              {description}
            </p>
          ) : null}
          {detail ? (
            <div
              aria-label={detailLabel}
              className="mt-3 max-w-full break-words rounded-[9px] bg-[var(--a-surface-2)] px-2.5 py-1.5 font-mono text-[11px] text-[var(--a-text-soft)]"
            >
              {detail}
            </div>
          ) : null}
          {resolvedPrimaryAction || secondaryAction ? (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              {resolvedPrimaryAction}
              {secondaryAction}
            </div>
          ) : null}
          {footer ? (
            <div
              className={cn(
                'mt-4 max-w-xl text-[12px] leading-5 text-[var(--a-text-faint)]',
                scale === 'page' && 'w-full border-t border-[var(--a-line-soft)] pt-4',
              )}
            >
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

DataState.displayName = 'DataState';
